import React, { useState } from 'react';
import { GridWrapper, StyledECardColumn, StyledECardColumns } from '@components/GalleryComponents/GalleryGrid/GalleryGrid.styles';
import { GalleryECardModal } from '@components/GalleryComponents/GalleryECardModal/GalleryECardModal';
import { PhotoECardModal } from '@components/GalleryComponents/PhotoECardModal/PhotoECardModal';
import { GalleryECardCell } from '@components/GalleryComponents/GalleryGrid/GalleryECardCell';
import { useTranslation } from 'react-i18next';

const analytics = require('../../../../static/js/joy');
interface Props
  extends Readonly<{
    cards: MarketingData;
    productType: GalleryProductType;
    /**
     * Is viewing items that have matching prints
     */
    hasPrints?: boolean;
    defaultProductType?: GalleryProductType;
    showViewDemoSite?: boolean;
  }> {}

const sessionViewMapByProductType = new Map<GalleryProductType, { totalViewCount: number; viewCountByThemeId: Record<string, number> }>();

export const GalleryGrid: React.FC<Props> = ({ cards, hasPrints, productType, defaultProductType, showViewDemoSite }) => {
  const { t } = useTranslation('galleryPage');
  const { designs } = t('productTypes');
  const [isDisplayCardDetails, setCardDetailModalToggle] = useState(false);
  const [cardDetails, setCardDetails] = useState<GalleryECard>({
    title: '',
    variationType: '',
    imageUrl: '',
    colors: [''],
    font: '',
    printUrl: '',
    tags: [''],
    vendor: '',
    productType,
    variantColor: '',
    variantName: '',
    variantThemeId: '',
    theme: {
      name: '',
      themeId: '',
      marketingData: [],
    },
  });

  const handleCardClick = (card: GalleryECard, idx: number) => {
    const themeId = card.theme?.themeId ?? card.variantThemeId;
    const sessionView = sessionViewMapByProductType.get(productType) ?? { totalViewCount: 0, viewCountByThemeId: {} };
    sessionView.totalViewCount++;

    const themeViewCount = sessionView.viewCountByThemeId[themeId] ?? 0;
    const nextThemeViewCount = themeViewCount + 1;
    sessionView.viewCountByThemeId[themeId] = nextThemeViewCount;
    sessionViewMapByProductType.set(productType, sessionView);

    analytics.segment.sendTelemetryEvent('galleryDesignClicked', {
      label: card.title,
      value: card.title,
      productCatalogItemPosition: idx,
      extraInfo: {
        font: card.font,
        websiteLayout: card.variationType,
        themeId: card.theme?.themeId ?? card.variantThemeId,
        isComingSoon: card.isComingSoon,
        totalSessionViewCountByProductType: sessionView.totalViewCount,
        themeViewCountByProductType: nextThemeViewCount,
      },
    });
    setCardDetails(card);
    setCardDetailModalToggle(true);
  };

  const handleCloseModal = () => {
    setCardDetailModalToggle(false);
  };

  // When viewing STD or Invitations, disable the print design redirect link if the card is joy printable
  const shouldDisablePrintDesignRedirectLink =
    hasPrints && (productType === 'Invitation' || productType === 'Save the Date') && cardDetails.isJoyPrintable;

  return (
    <GridWrapper>
      <StyledECardColumns multiline>
        {cards.map((card, index) => {
          card.productType = cardDetails.productType;
          return (
            <StyledECardColumn tablet={'six'} desktop={'four'} mobile={card.productType === designs ? 'full' : 'half'} key={index}>
              <GalleryECardCell index={index} card={card} onCardClick={(cd) => handleCardClick(cd, index)} />
            </StyledECardColumn>
          );
        })}
      </StyledECardColumns>
      {isDisplayCardDetails && !cardDetails.isPhotoECards && (
        <GalleryECardModal
          productType={productType}
          handleClose={handleCloseModal}
          cardDetails={cardDetails}
          isModalOpen={isDisplayCardDetails}
          disablePrintDesignRedirectLink={shouldDisablePrintDesignRedirectLink}
          defaultProductType={defaultProductType}
          showViewDemoSite={showViewDemoSite}
        />
      )}
      {isDisplayCardDetails && cardDetails.isPhotoECards && (
        <PhotoECardModal
          productType={productType}
          handleClose={handleCloseModal}
          cardDetails={cardDetails}
          isModalOpen={isDisplayCardDetails}
        />
      )}
    </GridWrapper>
  );
};
